import { useState, useEffect, useRef } from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '../../ui/command';
import { ChevronsUpDown, Check, AlertTriangle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { INDIAN_BANKS } from '../../../utils/bankConstants';
import { checkAccountNumber } from '../../../services/api';

const BankDetailsSection = ({ bankForm, updateForm }) => {
  const [bankNameOpen, setBankNameOpen] = useState(false);
  const [duplicateWarning, setDuplicateWarning] = useState(null);
  const [checking, setChecking] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    const accNum = bankForm.accountNumber;
    if (!accNum || accNum.length < 4) {
      setDuplicateWarning(null);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setChecking(true);
      try {
        const res = await checkAccountNumber(accNum);
        if (res.data.exists) {
          setDuplicateWarning(res.data);
        } else {
          setDuplicateWarning(null);
        }
      } catch {
        setDuplicateWarning(null);
      } finally {
        setChecking(false);
      }
    }, 500);

    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [bankForm.accountNumber]);

  return (
    <div className="space-y-4 p-4 bg-white border rounded-lg">
      <h3 className="font-semibold text-sm text-slate-700">Bank Details</h3>
      <div>
        <Label>Account Number *</Label>
        <Input
          value={bankForm.accountNumber}
          onChange={(e) => updateForm('accountNumber', e.target.value.replace(/\D/g, ''))}
          placeholder="Numbers only"
          required
          data-testid="account-number-input"
          className={duplicateWarning ? 'border-amber-500 focus-visible:ring-amber-500' : ''}
        />
        {checking && (
          <p className="text-xs text-slate-400 mt-1">Checking...</p>
        )}
        {duplicateWarning && (
          <div className="flex items-start gap-2 mt-2 p-2.5 bg-amber-50 border border-amber-200 rounded-md" data-testid="duplicate-account-warning">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="text-xs text-amber-800">
              <span className="font-semibold">Duplicate found:</span> This account number already exists under vendor <span className="font-semibold">{duplicateWarning.vendorName}</span> — {duplicateWarning.bankName} ({duplicateWarning.companyName})
            </div>
          </div>
        )}
      </div>
      <div>
        <Label>Bank Name *</Label>
        <Popover open={bankNameOpen} onOpenChange={setBankNameOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" role="combobox" aria-expanded={bankNameOpen} className="w-full justify-between font-normal" data-testid="bank-name-combobox">
              {bankForm.bankName || "Search bank..."}
              <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-[400px] p-0" align="start">
            <Command>
              <CommandInput placeholder="Search bank name..." />
              <CommandList>
                <CommandEmpty>No bank found.</CommandEmpty>
                {INDIAN_BANKS.map(cat => (
                  <CommandGroup key={cat.category} heading={cat.category}>
                    {cat.banks.map(bank => (
                      <CommandItem key={bank} value={bank} onSelect={() => { updateForm('bankName', bank); setBankNameOpen(false); }}>
                        <Check className={cn("mr-2 h-4 w-4", bankForm.bankName === bank ? "opacity-100" : "opacity-0")} />
                        {bank}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </CommandList>
            </Command>
          </PopoverContent>
        </Popover>
      </div>
      <div>
        <Label>Company Name *</Label>
        <Input value={bankForm.companyName} onChange={(e) => updateForm('companyName', e.target.value.toUpperCase())} placeholder="COMPANY / BUSINESS NAME" data-testid="company-name-input" required />
      </div>
      <div>
        <Label>Region *</Label>
        <Input value={bankForm.region} onChange={(e) => updateForm('region', e.target.value)} required />
      </div>
      <div>
        <Label>IFSC Code *</Label>
        <Input value={bankForm.ifsc} onChange={(e) => updateForm('ifsc', e.target.value)} required />
      </div>
      <div>
        <Label>Account Holder Name</Label>
        <Input value={bankForm.holderName} onChange={(e) => updateForm('holderName', e.target.value)} />
      </div>
      <div>
        <Label>Account Type</Label>
        <Select value={bankForm.accountType} onValueChange={(val) => updateForm('accountType', val)}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="Savings">Savings</SelectItem>
            <SelectItem value="Current">Current</SelectItem>
            <SelectItem value="Salary">Salary</SelectItem>
            <SelectItem value="Company Current">Company Current</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  );
};

export default BankDetailsSection;
